import React, { useState, memo } from "react";
import { motion } from "framer-motion";
import { FiMapPin, FiClock } from "react-icons/fi";
import {
  TbMessage,
  TbPhone,
  TbSparkles,
  TbShieldCheck,
  TbSend,
} from "react-icons/tb";
import Card from "../../components/Card/Card";
import Button from "../../components/Button/Button";
import FeatureIcon from "../../components/FeatureIcon/FeatureIcon";
import CustomTextInput from "../../components/CustomTextInput/CustomTextInput";
import PopupModal from "../../components/PopupModal/PopupModal";
import AxiosInstance from "../../services/Api";
import styles from "./ContactSupport.module.css";
import Badge from "../../components/Badge/Badge";
import SEO from "../../seo/SEO";
import seoConfig from "../../seo/seoConfig";

const LOCATIONS = [
  {
    region: "India",
    city: "Chennai",
    address:
      "Module 308, Second Floor,\nNSIC Software Technology Business Park,\nSector B-24, Guindy Industrial Estate,\nEkkattuthangal, Chennai,\nTamil Nadu - 600032",
    phone: "+91 93447 72393, 044 - 2364 4142",
    img: "https://images.unsplash.com/photo-1661366698983-3cb843219300?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Y2hlbm5haXxlbnwwfHwwfHx8MA%3D%3D",
  },
];

// const SUPPORT_CHANNELS = [
//   {
//     icon: TbMessage,
//     iconTint: "cyan",
//     title: "Live Concierge",
//     subtitle: "Response in < 2 mins",
//   },
//   {
//     icon: TbPhone,
//     iconTint: "blue",
//     title: "Priority Line",
//     subtitle: "24/7 Global Access",
//   },
//   {
//     icon: TbSparkles,
//     iconTint: "purple",
//     title: "AI Assistant",
//     subtitle: "Self-serve solutions",
//   },
// ];

/* ============================================================
   FORM CONFIG & VALIDATION REGEXES
============================================================ */
const INITIAL_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  message: "",
};

// Strict email: standard RFC-ish format
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Names: letters, spaces, hyphens, apostrophes, periods only (no numbers, no special chars)
const NAME_REGEX = /^[a-zA-Z\s'.-]+$/;
// Phone (after stripping spaces): optional leading + then only digits
const PHONE_DIGITS_REGEX = /^\+?\d+$/;
// Emoji detection — covers virtually all emoji unicode ranges
const EMOJI_REGEX = /\p{Extended_Pictographic}/u;

const containsEmoji = (str = "") => EMOJI_REGEX.test(str);

/* ============================================================
   VALIDATION
============================================================ */
const validateForm = (data) => {
  const errors = {};

  // ---------- First Name ----------
  const firstName = data.first_name.trim();
  if (!firstName) {
    errors.first_name = "First name is required";
  } else if (containsEmoji(data.first_name)) {
    errors.first_name = "Emojis are not allowed in first name";
  } else if (!NAME_REGEX.test(firstName)) {
    errors.first_name =
      "First name cannot contain numbers or special characters";
  } else if (firstName.length < 2) {
    errors.first_name = "First name must be at least 2 characters";
  } else if (firstName.length > 50) {
    errors.first_name = "First name must be 50 characters or less";
  }

  // ---------- Last Name ----------
  const lastName = data.last_name.trim();
  if (!lastName) {
    errors.last_name = "Last name is required";
  } else if (containsEmoji(data.last_name)) {
    errors.last_name = "Emojis are not allowed in last name";
  } else if (!NAME_REGEX.test(lastName)) {
    errors.last_name = "Last name cannot contain numbers or special characters";
  } else if (lastName.length > 50) {
    errors.last_name = "Last name must be 50 characters or less";
  }

  // ---------- Email ----------
  const email = data.email.trim();
  if (!email) {
    errors.email = "Email is required";
  } else if (containsEmoji(data.email)) {
    errors.email = "Emojis are not allowed in email";
  } else if (/\s/.test(email)) {
    errors.email = "Email cannot contain spaces";
  } else if (!EMAIL_REGEX.test(email)) {
    errors.email = "Enter a valid email address";
  } else if (email.length > 100) {
    errors.email = "Email must be 100 characters or less";
  }

  // ---------- Phone ----------
  const phoneRaw = data.phone;
  if (!phoneRaw.trim()) {
    errors.phone = "Phone number is required";
  } else if (containsEmoji(data.phone)) {
    errors.phone = "Emojis are not allowed in phone number";
  } else if (/\s/.test(phoneRaw)) {
    errors.phone = "Enter a valid phone number";
  } else if (/^[^\d+]/.test(phoneRaw)) {
    errors.phone = "Enter a valid phone number";
  } else {
    if (!PHONE_DIGITS_REGEX.test(phoneRaw)) {
      errors.phone = "Enter a valid phone number";
    } else {
      const digitsOnly = phoneRaw.replace(/^\+/, "");
      if (digitsOnly.length < 10) {
        errors.phone = "Phone number must be at least 10 digits";
      } else if (digitsOnly.length > 15) {
        errors.phone = "Phone number must be 15 digits or less";
      }
    }
  }

  // ---------- Message ----------
  const message = data.message.trim();
  if (!message) {
    errors.message = "Please enter a message";
  } else if (containsEmoji(data.message)) {
    errors.message = "Emojis are not allowed in message";
  } else if (message.length < 10) {
    errors.message = "Message should be at least 10 characters";
  } else if (message.length > 1000) {
    errors.message = "Message must be 1000 characters or less";
  }

  return errors;
};

/* ============================================================
   CONTACT FORM — isolated, plain <div> wrapper (no Card)
============================================================ */
const ContactForm = memo(function ContactForm() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [popup, setPopup] = useState({
    open: false,
    title: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    try {
      const cleanEmail = formData.email.trim();
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: cleanEmail,
        phone: formData.phone.trim(),
        message: formData.message.trim(),
        created_by: cleanEmail,
      };

      const { data } = await AxiosInstance.post(
        "/roll/contact-create",
        payload,
      );

      if (data?.success) {
        setPopup({
          open: true,
          title: "Message Sent",
          message:
            data.message ||
            "Submitted successfully. Our team will get back to you shortly.",
        });
        setFormData(INITIAL_FORM);
      } else {
        setPopup({
          open: true,
          title: "Submission Failed",
          message:
            data?.message ||
            "We couldn't send your message. Please try again in a moment.",
        });
      }
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to reach our servers. Please check your connection and try again.";
      setPopup({
        open: true,
        title: "Network Error",
        message: apiMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const closePopup = () => setPopup((prev) => ({ ...prev, open: false }));

  return (
    <>
      <SEO {...seoConfig.contact} />
      {/* 🔁 Plain <div> with same class — Card replaced to fix focus jump */}
      <div className={styles.formCard}>
        <h2 className={styles.formTitle}>Send a Message</h2>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <CustomTextInput
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="John"
                error={errors.first_name}
                required
                autoComplete="given-name"
              />
            </div>
            <div className="col-12 col-sm-6">
              <CustomTextInput
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Doe"
                error={errors.last_name}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <CustomTextInput
            label="Email Address"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@university.edu"
            error={errors.email}
            required
            autoComplete="email"
          />

          <CustomTextInput
            label="Phone Number"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 93447 72393"
            error={errors.phone}
            required
            autoComplete="tel"
          />

          <CustomTextInput
            label="Message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="How can our intelligence help yours?"
            error={errors.message}
            required
            multiline
            rows={5}
          />

          <Button
            type="submit"
            variant="gradient"
            size="lg"
            fullWidth
            iconRight={<TbSend />}
            disabled={submitting}
          >
            {submitting ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </div>

      <PopupModal
        open={popup.open}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
        onOk={closePopup}
      />
    </>
  );
});

/* ============================================================
   MAIN PAGE COMPONENT
============================================================ */
const ContactSupport = () => {
  return (
    <>
      <SEO {...seoConfig.contact} />
      <div className={styles.page}>
        <section className={`page-container ${styles.heroSection}`}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className={styles.heroText}
          >
            <Badge variant="cyan">We're Here to Help</Badge>
            <h1 className={styles.heroTitle}>
              Let's evolve <span className="gradientText mt-2">together.</span>
            </h1>
            <p className={styles.heroLead}>
              Connect with Team iFillip to discover how our intelligent learning
              solutions can transform teaching, engagement, and academic
              excellence.
            </p>
          </motion.div>

          <div className={`row g-4 ${styles.mainRow}`}>
            {/* LEFT — Form + Support channels */}
            <div className="col-12 col-lg-7">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <ContactForm />
              </motion.div>

              {/* Support channels */}
              {/* <div className={`row g-3 ${styles.channelsRow}`}>
              {SUPPORT_CHANNELS.map((ch, i) => (
                <div key={ch.title} className="col-12 col-sm-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                  >
                    <Card
                      variant="soft"
                      padding="md"
                      className={styles.channelCard}
                    >
                      <FeatureIcon
                        icon={ch.icon}
                        tint={
                          ch.iconTint === "cyan"
                            ? "cyanSolid"
                            : ch.iconTint === "blue"
                              ? "purpleSolid"
                              : "purpleSolid"
                        }
                        size="md"
                      />
                      <h4 className={styles.channelTitle}>{ch.title}</h4>
                      <p className={styles.channelSubtitle}>{ch.subtitle}</p>
                    </Card>
                  </motion.div>
                </div>
              ))}
            </div> */}
            </div>

            {/* RIGHT — Location cards + Privacy */}
            <div className="col-12 col-lg-5">
              <div className={styles.locations}>
                {LOCATIONS.map((loc, i) => (
                  <motion.div
                    key={loc.city}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                  >
                    <Card
                      variant="default"
                      padding="sm"
                      className={styles.locCard}
                    >
                      <div
                        className={styles.locImg}
                        style={{ backgroundImage: `url(${loc.img})` }}
                      >
                        <span className={styles.regionPill}>{loc.region}</span>
                      </div>
                      <div className={styles.locBody}>
                        <h3 className={styles.locCity}>{loc.city}</h3>
                        <div className={styles.locRow}>
                          <FiMapPin color="#8B5CF6" />
                          <span style={{ whiteSpace: "pre-line" }}>
                            {loc.address}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactSupport;
